import type { ReactNode } from 'react';
import { Button, Icon, Modal } from '../primitives';
import { useMountSound } from '../hooks/useMountSound';
import { useMountVibrate } from '../hooks/useMountVibrate';
import { AdvanceTrigger } from './AdvanceTrigger';
import './semantic.groupA.css';

/**
 * Foundation semantic: JudgementModal（Phase D §4.4 / Phase B 凍結対象 API）
 *
 * Visual Grammar:
 *  - オーバーレイ + 中央アイコンバッジ + タイトル + 説明 + 進行ボタン の共通レイアウト。
 *  - 配色は type に応じて success/failure トークンへ振り分け（mood に追従）。
 *
 * 構成:
 *  - Modal primitive をオーバーレイ/パネルとして使う。Modal の title prop は使わず、
 *   内部レイアウト（.mg-judgement）でタイトル h2 を独自に並べる（typography トークン適用）。
 *  - フッターの進行ボタンは AdvanceTrigger（variant=type）で統一する。
 *  - onRetry があれば「やり直す」を Button(secondary) で並べる。
 *
 * アイコン:
 *  - 成功: lucide Check
 *  - 失敗: lucide TriangleAlert（lucide-react の AlertTriangle は icons namespace のキーに
 *   含まれないため、同じグリフのエイリアス TriangleAlert を使う）
 *  - 任意で icon prop により上書き可能。
 *
 * 規約:
 *  - hex 直書き禁止。色は mood トークン経由（semantic.groupA.css 側）。
 *  - アイコンは Icon primitive 経由（直接 lucide-react を呼ばない）。
 */
export type JudgementModalType = 'success' | 'failure';

export interface JudgementModalProps {
  /** モーダル表示有無。false の間は Modal 内で何も描画しない */
  open: boolean;
  /** 判定種別。色トーンと既定アイコンが切り替わる */
  type: JudgementModalType;
  /** 太字タイトル（必須）。例: 「正解!」「不正解…」 */
  title: string;
  /** 説明文（任意）。1〜2 行程度を想定 */
  description?: string;
  /** 中央バッジのアイコン差し替え（既定: success=Check / failure=TriangleAlert） */
  icon?: ReactNode;
  /** 進行ボタンのラベル。未指定時は「次へ進む」 */
  buttonLabel?: string;
  /** 進行ボタン押下時のコールバック（Visual Grammar: 進行） */
  onSubmit: () => void;
  /** やり直しコールバック。指定時のみ「やり直す」ボタンを表示する */
  onRetry?: () => void;
  /** やり直しボタンのラベル。未指定時は「やり直す」 */
  retryLabel?: string;
}

export function JudgementModal({
  open,
  type,
  title,
  description,
  icon,
  buttonLabel,
  onSubmit,
  onRetry,
  retryLabel = 'やり直す',
}: JudgementModalProps) {
  // R2 Wave2B: 成功モーダルが表示された瞬間に success 音（mount 時 1 回・mood は data-mood 追従）。
  // open かつ type==='success' の時だけ鳴らす。失敗モーダルは FailOverlay 側の責務（ここでは鳴らさない）。
  // 成功音はこの JudgementModal に集約する（JudgementBurst 側は鳴らさない＝二重発火を構造的に防ぐ）。
  useMountSound('success', open && type === 'success');
  // R2 Wave3B: 成功表示の瞬間に success 振動を添える（音と同じ enabled 条件・対応端末のみ・音は不変）。
  useMountVibrate('success', open && type === 'success');

  // 既定アイコン: 成功は Check、失敗は TriangleAlert（Visual Grammar 固定）。
  // 注: lucide-react v0.4x 系では AlertTriangle と Unlock は ESM 直接 export のエイリアス
  // としては存在するが `icons` namespace（Icon primitive が参照する型）のキーには含まれない。
  // そのため Icon primitive 経由で叩ける同義名 TriangleAlert を採用する。
  const defaultIcon =
    type === 'success' ? <Icon name="Check" size={36} /> : <Icon name="TriangleAlert" size={36} />;
  const renderedIcon = icon !== undefined ? icon : defaultIcon;

  const badgeClass =
    type === 'success'
      ? 'mg-judgement__badge mg-judgement__badge--success'
      : 'mg-judgement__badge mg-judgement__badge--failure';

  return (
    <Modal open={open}>
      <div className="mg-judgement">
        <div className={badgeClass} aria-hidden>
          {renderedIcon}
        </div>
        <h2 className="mg-judgement__title">{title}</h2>
        {description ? (
          <p className="mg-judgement__description">{description}</p>
        ) : null}
        <div className="mg-judgement__actions">
          <AdvanceTrigger
            variant={type}
            label={buttonLabel ?? '次へ進む'}
            onAdvance={onSubmit}
          />
          {onRetry ? (
            <Button variant="secondary" size="md" onClick={onRetry}>
              {retryLabel}
            </Button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
